import { useState } from 'react'

import type { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Alert, VStack } from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'
import { SingularDayForm } from '~/pages/AdminSingularDay/components/SingularDayForm'
import { useCreateSingularDay } from '~/pages/AdminSingularDay/hooks/useCreateSingularDay'
import { normalizeAreaCodesForSubmit } from '~/pages/AdminSingularDay/models/options'
import {
  type SingularDayFormInput,
  SingularDayFormSchema,
  type SingularDayFormValues,
  defaultSingularDayFormValues,
} from '~/pages/AdminSingularDay/models/schema'
import { mapSingularDaySaveError } from '~/pages/AdminSingularDay/models/serverError'

type Props = {
  isOpen: boolean
  /** 日付の初期値 (design AC-004: 選択中年度の 4/1、 今日が年度内なら今日)。 */
  initialDate: Date
  /** カテゴリフィルタ選択中はそのカテゴリを既定にする (spec AC-004)。 */
  filterCategory?: SingularDayCategory
  onClose: () => void
  /** 保存後に同一日付の全エリア共通レコードが検知された時のコールバック (AC-MSD-013a)。 */
  onInfo?: (name: string) => void
  /** 「特異グループ名を編集」 (一括エディタ) を開く。 */
  onEditGroupNames?: () => void
}

/**
 * 特異日登録モーダル (design demand/special-day-calendar.html「特異日を追加」)。
 *
 * 日付は入力可 (AC-004)。 カテゴリ既定=その他 (フィルタ選択中はそのカテゴリ)、
 * 需要扱い既定=特異。 Server error (= AC-09 同一日付重複を含む) は form 上部に
 * 表示する (setError('root.serverError'))。
 */
export const CreateSingularDayModal = ({
  isOpen,
  initialDate,
  filterCategory,
  onClose,
  onInfo,
  onEditGroupNames,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useCreateSingularDay()

  const [dateValue, setDateValue] = useState<CivilDate | undefined>(() => ({
    year: initialDate.getFullYear(),
    month: initialDate.getMonth() + 1,
    day: initialDate.getDate(),
  }))

  const {
    register,
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<SingularDayFormInput, unknown, SingularDayFormValues>({
    resolver: zodResolver(SingularDayFormSchema),
    mode: 'onBlur',
    defaultValues: defaultSingularDayFormValues(filterCategory),
  })

  const handleClose = (): void => {
    reset()
    setDateValue({
      year: initialDate.getFullYear(),
      month: initialDate.getMonth() + 1,
      day: initialDate.getDate(),
    })
    onClose()
  }

  const onSubmit = async (values: SingularDayFormValues): Promise<void> => {
    if (dateValue === undefined) {
      setError('root.serverError', {
        message: t('page:adminSingularDay.serverError.dateRequired'),
      })
      return
    }
    try {
      const resp = await mutateAsync({
        date: civilDateToProtoDate(dateValue),
        name: values.name,
        category: values.category,
        demandBehavior: values.demandBehavior,
        singularGroup: values.singularGroup,
        areaCodes: normalizeAreaCodesForSubmit(values.areaCodes),
      })
      if (
        resp.overriddenAllAreaName !== undefined &&
        resp.overriddenAllAreaName !== ''
      ) {
        onInfo?.(resp.overriddenAllAreaName)
      }
      reset()
      onClose()
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({ title: t('page:adminSingularDay.toast.saved') })
    } catch (err) {
      setError('root.serverError', {
        message: mapSingularDaySaveError(err, t),
      })
    }
  }

  const handleSave = (): void => {
    void handleSubmit(onSubmit, (formErrors) => {
      // 項目脇に FieldErrorText が出るエラーは alert に再掲しない (重複表示になる)。
      const alertMessage = resolveFormErrorAlertMessage(
        formErrors,
        t('page:adminSingularDay.serverError.validation'),
      )
      if (alertMessage !== undefined) {
        setError('root.serverError', { message: alertMessage })
      } else {
        // 直前の失敗で出した alert が残らないよう明示的に消す
        // (項目脇の表示だけに切り替わるため)。
        clearErrors('root.serverError')
      }
    })()
  }

  const footer = (
    <SaveCancelFooter
      isPending={isPending}
      onCancel={handleClose}
      onSave={handleSave}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={t('page:adminSingularDay.modal.createTitle')}
    >
      <VStack align="stretch" gap={4}>
        {errors.root?.serverError !== undefined && (
          <Alert status="error" title={errors.root.serverError.message} />
        )}
        <SingularDayForm
          control={control}
          dateValue={dateValue}
          errors={errors}
          mode="create"
          onDateChange={setDateValue}
          onEditGroupNames={onEditGroupNames}
          register={register}
        />
      </VStack>
    </Modal>
  )
}
